import { CHANNEL_LABELS, cleanValue } from '../../domain/animationOps';
import { OpError } from '../../domain/projectOps';
import { isLocked, isShown } from '../../domain/sceneOps';
import {
  MAX_KEYFRAMES_PER_TRACK,
  TRANSFORM_CHANNELS,
  POSE_CHANNELS,
  formatIssues,
  type AnimationTrack,
  type Channel,
  type Character,
  type Keyframe,
  type Scene,
  type SceneObject,
} from '../../domain/schemas';
import { Rng } from '../../lib/random';
import { baseValue, trackValueAt } from '../timeline';
import { absolutePoints, previewPolyline } from './paths';
import { MAX_GENERATED_KEYS, MAX_TRAIL_POINTS, MotionPlanSchema, TEMPLATES, describeStep, resolvedParams, type Binding, type MotionPlan, type MotionPlanInput, type MotionStep, type TemplateId } from './spec';
import { generate } from './templates';

/**
 * The procedural motion compiler (Phase 3C).
 *
 * `compileMotion(plan, context)` is a pure function: it validates the plan, resolves every
 * step to frames, and runs the motion templates to produce ordinary keyframes. It never
 * mutates the project, never touches PixiJS, the clock, the network or audio, and never
 * calls Math.random (variation uses the plan's seed). Identical inputs give identical
 * keyframe frames and values. Ids are not generated here: tracks are identified by
 * (object, channel) and get ids when a plan is applied (see domain/motionOps.ts).
 *
 * Timing: each step starts per its timing mode (after the previous step, with/after a
 * referenced step, or at a time) plus its delay; targets of one step start `stagger`
 * seconds apart. A step without a duration lasts its template's default (× repetitions);
 * one followed by a step on the same target that starts at an explicit time lasts until
 * then ("jump at frame 48 and land at frame 72"); flexible steps share what is left of the
 * plan's `totalSeconds`; and a flexible step running `with` another lasts as long as it.
 * A step that ends exactly at the animation's end (one frame past its last frame) is placed
 * on the last frame.
 *
 * Continuity: steps are generated in time order, and each reads its targets' state at its
 * start frame from what came before (earlier steps, else the existing animation, else the
 * base object). Two steps writing the same object channel over overlapping frames are an
 * error; sharing a boundary frame is how sequential steps join.
 */
export class MotionError extends OpError {
  constructor(
    message: string,
    readonly issues: string[] = [message],
  ) {
    super(message);
    this.name = 'MotionError';
  }
}

export interface CompileContext {
  scene: Scene;
  characters: readonly Character[];
  /** Timing of the animation the plan is compiled for. */
  timing: { fps: number; durationFrames: number };
  /** Tracks already in that animation (start state for continuity). */
  existing?: readonly AnimationTrack[] | null;
  binding: Binding;
  /** Let the plan run past the animation's last frame (the caller extends the animation). */
  allowOverflow?: boolean;
  /** Shifts the whole plan by this many frames (the append policy). */
  frameOffset?: number;
}

export interface MotionWarning {
  stepId: string | null;
  message: string;
}

export interface CompiledTrack {
  objectId: string;
  channel: Channel;
  keyframes: Keyframe[];
  /** First and last generated frame. */
  from: number;
  to: number;
  /** Ids of the steps that wrote this track. */
  steps: string[];
}

export interface StepTiming {
  stepId: string;
  index: number;
  template: TemplateId;
  label: string;
  slot: string;
  objectId: string;
  objectName: string;
  start: number;
  end: number;
  channels: Channel[];
}

export interface CompiledMotion {
  tracks: CompiledTrack[];
  warnings: MotionWarning[];
  steps: StepTiming[];
  /** Earliest and last frame written by the plan. */
  startFrame: number;
  endFrame: number;
  /** Frames the animation needs to hold the plan (endFrame + 1). */
  requiredFrames: number;
  keyCount: number;
  /** Anchor-point trajectories of moving targets (for the canvas preview). */
  trails: { objectId: string; points: [number, number][] }[];
  /** Path steps' paths (for the canvas preview). */
  paths: { stepId: string; objectId: string; points: [number, number][] }[];
  plan: MotionPlan;
}

const CHANNEL_ORDER = new Map<Channel, number>([...TRANSFORM_CHANNELS, ...POSE_CHANNELS].map((c, i) => [c as Channel, i]));
const MIN_STEP_FRAMES = 2;

/** Parses a plan, reporting every problem readably. */
export function parsePlan(input: MotionPlanInput): MotionPlan {
  const parsed = MotionPlanSchema.safeParse(input);
  if (!parsed.success) {
    const issues = formatIssues(parsed.error);
    throw new MotionError(`The motion plan is invalid: ${issues[0] ?? 'unknown problem'}`, issues);
  }
  return parsed.data;
}

interface Bound {
  slot: string;
  object: SceneObject;
  character: Character | undefined;
}

/** Resolves slots to scene objects (by stable id) and checks they can be animated. */
export function bindTargets(plan: MotionPlan, scene: Scene, characters: readonly Character[], binding: Binding, warnings: MotionWarning[] = []): Map<string, Bound> {
  const out = new Map<string, Bound>();
  const issues: string[] = [];
  const used = new Set(plan.steps.flatMap((s) => s.targets));
  for (const slot of plan.slots) {
    if (!used.has(slot.name)) continue;
    const id = binding[slot.name];
    if (!id) {
      issues.push(`Choose an object for “${slot.label}”.`);
      continue;
    }
    const object = scene.objects.find((o) => o.id === id);
    if (!object) {
      issues.push(`“${slot.label}” refers to an object that is not in “${scene.name}”.`);
      continue;
    }
    if (slot.kind === 'character' && object.type !== 'character') {
      issues.push(`“${slot.label}” must be a character instance, but “${object.name}” is a ${object.type}.`);
      continue;
    }
    if (isLocked(scene, object)) {
      issues.push(`“${object.name}” is locked (or on a locked layer). Unlock it to animate it.`);
      continue;
    }
    const character = object.type === 'character' ? characters.find((c) => c.id === object.characterId) : undefined;
    if (object.type === 'character' && !character) {
      issues.push(`“${object.name}” uses a character definition that no longer exists.`);
      continue;
    }
    if (!isShown(scene, object)) warnings.push({ stepId: null, message: `“${object.name}” is hidden; its motion is added but it only shows once it is visible (a fade in makes a hidden object visible).` });
    out.set(slot.name, { slot: slot.name, object, character });
  }
  for (const step of plan.steps) {
    if (!TEMPLATES[step.template].characterOnly) continue;
    for (const t of step.targets) {
      const b = out.get(t);
      if (b && b.object.type !== 'character') issues.push(`${TEMPLATES[step.template].label} poses a character's limbs; “${b.object.name}” is a ${b.object.type}.`);
    }
  }
  if (issues.length) throw new MotionError(issues[0]!, issues);
  return out;
}

/** Seconds → step start/duration (see the timing rules above). */
export function resolveStepTimes(plan: MotionPlan): { start: number; duration: number }[] {
  const steps = plan.steps;
  const index = new Map(steps.map((s, i) => [s.id, i]));
  const base = steps.map((s) => {
    if (s.duration !== null) return s.duration;
    const info = TEMPLATES[s.template];
    if (info.cyclic && s.repeat > 1) return (info.cycleSeconds ?? info.defaultSeconds) * s.repeat;
    return info.cyclic ? info.defaultSeconds : info.defaultSeconds * s.repeat;
  });
  const fillable = steps.map((s) => plan.totalSeconds !== null && s.duration === null && TEMPLATES[s.template].flexible && s.timing.mode !== 'with');
  const place = (durations: number[]) => {
    const out: { start: number; duration: number }[] = [];
    steps.forEach((s, i) => {
      const prev = out[i - 1];
      const ref = s.timing.ref !== null ? out[index.get(s.timing.ref)!] : prev;
      let start = 0;
      if (s.timing.mode === 'at') start = s.timing.at ?? 0;
      else if (s.timing.mode === 'with') start = ref ? ref.start : 0;
      else if (s.timing.mode === 'after') start = ref ? ref.start + ref.duration : 0;
      else start = prev ? prev.start + prev.duration + stagger(steps[i - 1]!) : 0;
      start += s.timing.delay;
      let duration = durations[i]!;
      // A flexible step running alongside another lasts as long as it.
      if (s.duration === null && s.timing.mode === 'with' && ref && TEMPLATES[s.template].flexible) duration = ref.duration;
      // Until the next step on the same target that starts at an explicit time.
      if (s.duration === null) {
        const next = steps.slice(i + 1).find((n) => n.targets.some((t) => s.targets.includes(t)));
        if (next && next.timing.mode === 'at' && next.timing.at !== null) {
          const gap = next.timing.at + next.timing.delay - start;
          if (gap > 0.05) duration = gap;
        }
      }
      out.push({ start, duration });
    });
    return out;
  };
  if (!fillable.some(Boolean)) return place(base);
  const probe = place(base.map((d, i) => (fillable[i] ? 0 : d)));
  const end = Math.max(...probe.map((t, i) => t.start + t.duration + stagger(steps[i]!)));
  const left = plan.totalSeconds! - end;
  const share = left > 0 ? left / fillable.filter(Boolean).length : 0;
  return place(base.map((d, i) => (fillable[i] ? (share > 0 ? share : d) : d)));
}

const stagger = (s: MotionStep) => s.stagger * (s.targets.length - 1);

/** Compiles a motion plan into keyframes. Throws MotionError (with every issue) when it cannot. */
export function compileMotion(input: MotionPlanInput, ctx: CompileContext): CompiledMotion {
  const plan = parsePlan(input);
  const warnings: MotionWarning[] = [];
  const bound = bindTargets(plan, ctx.scene, ctx.characters, ctx.binding, warnings);
  const { fps, durationFrames } = ctx.timing;
  const offset = ctx.frameOffset ?? 0;
  const times = resolveStepTimes(plan);
  const slotLabel = (name: string) => plan.slots.find((s) => s.name === name)?.label ?? name;

  // ---- Frame ranges of every (step, target).
  interface Job {
    step: MotionStep;
    index: number;
    target: Bound;
    targetIndex: number;
    f0: number;
    f1: number;
  }
  const jobs: Job[] = [];
  const issues: string[] = [];
  plan.steps.forEach((step, index) => {
    const t = times[index]!;
    step.targets.forEach((slot, targetIndex) => {
      const start = t.start + step.stagger * targetIndex;
      let f0 = Math.round(start * fps) + offset;
      let f1 = Math.round((start + t.duration) * fps) + offset;
      // Ending exactly at the end of the animation ("4 s of motion in a 4 s animation") lands
      // on its last frame.
      if (f1 === durationFrames && !ctx.allowOverflow) f1 = durationFrames - 1;
      const info = TEMPLATES[step.template];
      // Seeded de-synchronisation of cyclic group motion (only with variation).
      if (step.variation > 0 && info.cyclic && step.targets.length > 1) {
        const rng = new Rng(seedOf(plan, step, index, targetIndex) ^ 0x9e3779b9);
        f0 += Math.round(rng.float() * step.variation * Math.min((info.cycleSeconds ?? 1) * fps, (f1 - f0) / 4));
      }
      if (f1 - f0 < MIN_STEP_FRAMES) issues.push(`Step ${index + 1} (${info.label}) is too short: it needs at least ${MIN_STEP_FRAMES} frames.`);
      if (f1 > durationFrames - 1 && !ctx.allowOverflow) {
        issues.push(`Step ${index + 1} (${info.label}) ends at frame ${f1} (${(f1 / fps).toFixed(2)} s), after the animation's last frame ${durationFrames - 1}. Shorten the steps, or allow extending the animation.`);
      }
      jobs.push({ step, index, target: bound.get(slot)!, targetIndex, f0, f1 });
    });
  });
  if (issues.length) throw new MotionError(issues[0]!, issues);

  // ---- Generate in time order so each step continues from what precedes it.
  const generated = new Map<string, { objectId: string; channel: Channel; keys: Keyframe[]; ranges: { index: number; from: number; to: number }[]; steps: Set<string> }>();
  const existing = new Map<string, AnimationTrack>((ctx.existing ?? []).map((t) => [`${t.objectId}/${t.channel}`, t]));
  const valueAt = (object: SceneObject, channel: Channel, frame: number): number => {
    const g = generated.get(`${object.id}/${channel}`);
    if (g && g.keys.length && g.keys[0]!.frame <= frame) return trackValueAt({ id: '', objectId: object.id, channel, keyframes: g.keys }, frame)!;
    const e = existing.get(`${object.id}/${channel}`);
    return (e && trackValueAt(e, frame)) ?? baseValue(object, channel);
  };
  const timings: StepTiming[] = [];
  const pathStarts = new Map<Job, { x: number; y: number }>();
  const order = [...jobs].sort((a, b) => a.f0 - b.f0 || a.index - b.index || a.targetIndex - b.targetIndex);
  for (const job of order) {
    const { step, target } = job;
    const info = TEMPLATES[step.template];
    const params = resolvedParams(step);
    const easing = step.easing ?? info.defaultEasing;
    const seconds = (job.f1 - job.f0) / fps;
    const cycles = info.cyclic ? (step.repeat > 1 ? step.repeat : Math.max(1, Math.round(seconds / (info.cycleSeconds ?? seconds)))) : step.template === 'path' ? step.repeat : 1;
    const reps = info.cyclic || step.template === 'path' ? 1 : step.repeat;
    const rng = step.variation > 0 ? new Rng(seedOf(plan, step, job.index, job.targetIndex)) : null;
    const channels = new Set<Channel>();
    if (step.template === 'path') {
      const o = target.object;
      pathStarts.set(job, { x: valueAt(o, 'x', job.f0) + o.anchorX * o.width, y: valueAt(o, 'y', job.f0) + o.anchorY * o.height });
    }
    for (let r = 0; r < reps; r++) {
      const a = job.f0 + Math.round(((job.f1 - job.f0) * r) / reps);
      const b = job.f0 + Math.round(((job.f1 - job.f0) * (r + 1)) / reps);
      const out = generate(step.template, {
        object: target.object,
        character: target.character,
        f0: a,
        f1: b,
        fps,
        cycles,
        explicitCycles: step.repeat > 1,
        easing,
        p: params,
        path: step.path,
        at: (channel) => valueAt(target.object, channel, a),
        rng,
        variation: step.variation,
        scene: { width: ctx.scene.width, height: ctx.scene.height },
        warn: (message) => {
          if (!warnings.some((w) => w.stepId === step.id && w.message === message)) warnings.push({ stepId: step.id, message: `Step ${job.index + 1}: ${message}` });
        },
      });
      for (const [channel, keys] of Object.entries(out) as [Channel, Keyframe[]][]) {
        if (!keys.length) continue;
        channels.add(channel);
        merge(generated, target.object, channel, keys, job.index, step.id, plan);
      }
    }
    timings.push({ stepId: step.id, index: job.index, template: step.template, label: describeStep(step, slotLabel), slot: target.slot, objectId: target.object.id, objectName: target.object.name, start: job.f0, end: job.f1, channels: [...channels] });
  }

  // ---- Clean values, check limits, order deterministically.
  const objectOrder = new Map(ctx.scene.objects.map((o, i) => [o.id, i]));
  const tracks: CompiledTrack[] = [...generated.values()]
    .map((g) => {
      const keyframes = g.keys.map((k) => ({ frame: k.frame, value: cleanValue(g.channel, k.value), easing: g.channel === 'visible' ? ('step' as const) : k.easing }));
      return { objectId: g.objectId, channel: g.channel, keyframes, from: keyframes[0]!.frame, to: keyframes[keyframes.length - 1]!.frame, steps: [...g.steps] };
    })
    .sort((a, b) => objectOrder.get(a.objectId)! - objectOrder.get(b.objectId)! || CHANNEL_ORDER.get(a.channel)! - CHANNEL_ORDER.get(b.channel)!);
  const keyCount = tracks.reduce((n, t) => n + t.keyframes.length, 0);
  if (keyCount > MAX_GENERATED_KEYS) throw new MotionError(`The plan would generate ${keyCount} keyframes; the limit is ${MAX_GENERATED_KEYS}. Use fewer repetitions, targets or steps.`);
  for (const t of tracks) {
    if (t.keyframes.length > MAX_KEYFRAMES_PER_TRACK) throw new MotionError(`${CHANNEL_LABELS[t.channel]} of one object would get ${t.keyframes.length} keyframes; a track holds at most ${MAX_KEYFRAMES_PER_TRACK}.`);
  }
  const startFrame = tracks.length ? Math.min(...tracks.map((t) => t.from)) : 0;
  const endFrame = tracks.length ? Math.max(...tracks.map((t) => t.to)) : 0;

  // ---- Preview metadata.
  const trails: CompiledMotion['trails'] = [];
  for (const b of new Set([...bound.values()])) {
    const moving = tracks.filter((t) => t.objectId === b.object.id && (t.channel === 'x' || t.channel === 'y'));
    if (!moving.length) continue;
    const from = Math.min(...moving.map((t) => t.from));
    const to = Math.max(...moving.map((t) => t.to));
    const n = Math.min(MAX_TRAIL_POINTS - 1, Math.max(1, to - from));
    const points: [number, number][] = [];
    for (let i = 0; i <= n; i++) {
      const f = from + ((to - from) * i) / n;
      points.push([r2(valueAt(b.object, 'x', f) + b.object.anchorX * b.object.width), r2(valueAt(b.object, 'y', f) + b.object.anchorY * b.object.height)]);
    }
    trails.push({ objectId: b.object.id, points });
  }
  const paths: CompiledMotion['paths'] = [];
  for (const job of jobs) {
    if (job.step.template !== 'path' || !job.step.path) continue;
    const pts = absolutePoints(job.step.path, pathStarts.get(job)!);
    paths.push({ stepId: job.step.id, objectId: job.target.object.id, points: previewPolyline(job.step.path.kind, pts) });
  }
  timings.sort((a, b) => a.index - b.index || a.start - b.start);
  return { tracks, warnings, steps: timings, startFrame, endFrame, requiredFrames: endFrame + 1, keyCount, trails, paths, plan };
}

const r2 = (v: number) => Math.round(v * 100) / 100;

function seedOf(plan: MotionPlan, step: MotionStep, index: number, targetIndex: number): number {
  let h = (step.seed ?? plan.seed) >>> 0;
  for (const v of [index + 1, targetIndex + 1]) h = Math.imul(h ^ v, 2654435761) >>> 0;
  return h;
}

function merge(
  generated: Map<string, { objectId: string; channel: Channel; keys: Keyframe[]; ranges: { index: number; from: number; to: number }[]; steps: Set<string> }>,
  object: SceneObject,
  channel: Channel,
  keys: Keyframe[],
  index: number,
  stepId: string,
  plan: MotionPlan,
): void {
  const id = `${object.id}/${channel}`;
  let g = generated.get(id);
  if (!g) generated.set(id, (g = { objectId: object.id, channel, keys: [], ranges: [], steps: new Set() }));
  const from = keys[0]!.frame;
  const to = keys[keys.length - 1]!.frame;
  for (const r of g.ranges) {
    if (r.index !== index && from < r.to && to > r.from) {
      const other = plan.steps[r.index]!;
      throw new MotionError(
        `Steps ${r.index + 1} (${TEMPLATES[other.template].label}) and ${index + 1} (${TEMPLATES[plan.steps[index]!.template].label}) both animate ${CHANNEL_LABELS[channel]} of “${object.name}” during frames ${Math.max(from, r.from)}–${Math.min(to, r.to)}. Run them one after the other, or combine motions that use different channels.`,
      );
    }
  }
  g.ranges.push({ index, from, to });
  g.steps.add(stepId);
  const byFrame = new Map(g.keys.map((k) => [k.frame, k]));
  for (const k of keys) byFrame.set(k.frame, k);
  g.keys = [...byFrame.values()].sort((a, b) => a.frame - b.frame);
}
